import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router";
import { describeError, isApiError } from "@/api/client";
import { sessionQuery } from "@/api/session";
import { Button, InlineAlert, Spinner } from "@/components";
import { useDelayedVisibility } from "@/lib/hooks";
import { useAdoptWorkspaceTheme } from "../theme/useThemeSwitcher";
import styles from "./auth.module.css";

/** Login URL that returns to `path` after signing in. */
export function loginPath(path: string): string {
  return path && path !== "/" ? `/login?next=${encodeURIComponent(path)}` : "/login";
}

function SessionReady({ children }: { children: ReactNode }) {
  useAdoptWorkspaceTheme();
  return children;
}

/** Renders the app only for a signed-in user; GET /api/auth/me answering 401 sends you to /login. */
export function RequireSession({ children }: { children: ReactNode }) {
  const location = useLocation();
  const session = useQuery(sessionQuery);
  const showSpinner = useDelayedVisibility(session.isPending);

  if (session.isPending) {
    return showSpinner ? (
      <div className={styles.fullPage}>
        <Spinner size="md" label="Loading your workspace…" />
      </div>
    ) : null;
  }

  if (session.isError) {
    if (isApiError(session.error, 401)) {
      return <Navigate to={loginPath(`${location.pathname}${location.search}${location.hash}`)} replace />;
    }
    return (
      <div className={styles.fullPage}>
        <InlineAlert
          tone="danger"
          title="Can’t load your session"
          action={<Button onClick={() => void session.refetch()}>Retry</Button>}
        >
          {describeError(session.error)}
        </InlineAlert>
      </div>
    );
  }

  return <SessionReady>{children}</SessionReady>;
}
